import { observer } from 'mobx-react';
import useSWR from 'swr';
import useResolver from '../hooks/useResolver';
import AuthenticationService from '../services/AuthenticationService';
import React, { FC } from 'react';
import { Alert, GithubButton, GoogleButton } from '../ui';
import Heading from '../ui/Heading';
import HStack from '../ui/HStack';
import MicrosoftButton from '../ui/MicrosoftButton';
import { Skeleton, Table, Tbody, Td, Tr } from '@chakra-ui/react';
import FacebookButton from '../ui/FacebookButton';
import Button from '../ui/Button';
import { t } from '../i18n';

export interface ConnectedManageSignInMethodsProps {}

const ConnectedManageSignInMethods: FC<
  ConnectedManageSignInMethodsProps
> = () => {
  const authenticationService = useResolver(AuthenticationService);
  const { data: signInMethods, error } = useSWR('signInMethods', () =>
    authenticationService.getSignInMethods(),
  );

  const providers = [
    {
      name: 'Google',
      component: GoogleButton,
      linked: signInMethods?.some((method) => method.match(/^google/)),
      onLink: () => authenticationService.linkGoogleAccount(),
    },
    {
      name: 'GitHub',
      component: GithubButton,
      linked: signInMethods?.some((method) => method.match(/^github/)),
      onLink: () => authenticationService.linkGithubAccount(),
    },
    {
      name: 'Facebook',
      component: FacebookButton,
      linked: signInMethods?.some((method) => method.match(/^facebook/)),
      onLink: () => authenticationService.linkFacebookAccount(),
    },
    {
      name: 'Microsoft',
      component: MicrosoftButton,
      linked: signInMethods?.some((method) => method.match(/^microsoft/)),
      onLink: () => authenticationService.linkMicrosoftAccount(),
    },
  ];

  return (
    <div>
      {error && (
        <Alert status="error">{t('Failed to load sign in methods')}</Alert>
      )}
      <Heading size="md" mb={4}>
        {t('Sign-in methods')}
      </Heading>

      <Table>
        <Tbody>
          {providers.map(({ name, component: Component, linked, onLink }) => (
            <Tr key={name}>
              <Td>
                <HStack>
                  <Component onClick={onLink} />
                  <div>{name}</div>
                </HStack>
              </Td>
              <Td>
                {linked === undefined && (
                  <Skeleton height="32px" width="280px" />
                )}
                {linked === true && (
                  <Alert status="success">
                    {name} {t('is linked to your account')}
                  </Alert>
                )}
                {linked === false && (
                  <Button colorScheme="brand" onClick={onLink}>
                    {t('Connect')}
                  </Button>
                )}
              </Td>
              <Td></Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </div>
  );
};

export default observer(ConnectedManageSignInMethods);
